import { Field, Formik } from 'formik';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { ProcessTypeEnum } from '../../../constants/enums';
import { useStore } from '../../../contexts/store-provider';
import {
  getTenantAccessFromLocalStorage,
  getTenantIdFromLocalStorage,
} from '../../../helpers/localstorage-helper';
import { IProcessOptions } from '../../../models/forms/IAddUpdateProcessTemplate';
import { IActionFormatter } from '../../../models/ICommon';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikFormSelect from '../../../shared-components/FormikFormHandlers/FormikFormSelect';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import Label from '../../../shared-components/label/label';
import {
  DownIcon,
  RemoveIcon,
  RoundTabIcon,
  UpIcon,
} from '../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../translations/format-message';
import { ViewTypeOptions } from '../../../constants/options.constant';

interface IProps {
  id: number;
  initalValues: any;
  isLoading: boolean;
  processList: any;
  next: (newData: any, final?: boolean) => void;
  prev: (newData: any) => void;
  handleSaveAndNext: (newData: any, final?: boolean) => void;
  setCloseModal: (isClose: boolean) => void;
  setCurrentStep: (step: number) => void;
}

const StepTwo = (props: IProps) => {
  const { processStore, teplarPrintStore, processTemplateStore, productStore } =
    useStore();
  const {
    GetProcessByIdService,
    allProcessOptions,
    selectedProcessDetails,
    GetAllProcessService,
    resetProcessDetail,
    processState,
  } = processStore;

  const { GetTeplarByProductIdService } = teplarPrintStore;
  const {
    GetProcessTemplateByIdService,
    productProcessTemplates,
    allAvailableProcessTemplates,
    GetAllTemplatesService,
    resetProcessTemplateDetail,
  } = processTemplateStore;
  const processTemplateDetails: IProcessOptions[] = productProcessTemplates;
  const { allProductProcessTemplates, GetDimensionColumnService, setOldDeletedFiles, setNewDeletedFiles } =
    productStore;
  const { initalValues } = props;
  const [selectedProcesses, setSelectedProcesses] = useState<any[]>(
    initalValues?.Process?.length > 0 ? initalValues?.Process : []
  );
  const [loadStepThree, setLoadStepThree] = useState(false);
  const [isScroll, setIsScroll] = useState(true);
  const [selectedTemplateProcessIds, setSelectedTemplateProcessIds] = useState<
    number[]
  >([]);
  const [duplicateTemplateProcessId, setDuplicateTemplateProcessId] =
    useState<number | null>(null);
  const formikRef = useRef<any>(null);

  const processHeaders = [
    'process_name',
    'short_name',
    'category',
    'default_due_date',
    'standard_time',
    'view_type',
    'move',
  ];

  const normalizeAndReorder = (list: any[]) => {
    const buildTagAllocation = (dueDates: number = 0) => ({
      Id: -1,
      ProcessId: -1,
      DueDates: dueDates,
      ProcessOrder: 0,
      ProcessName: 'Tag Allocation',
      Type: -1,
      ShortName: 'ALLOCATE',
      StandardTime: '',
      ViewType: 1,
      fileIndices: [],
    });

    const buildTagRemove = (dueDates: number = 0) => ({
      Id: -1,
      ProcessId: -2,
      DueDates: dueDates,
      ProcessOrder: 0,
      ProcessName: 'Tag Remove',
      Type: -2,
      ShortName: 'REMOVE',
      StandardTime: '',
      ViewType: 1,
      fileIndices: [],
    });

    // fixed processes
    const existingTagAllocation = list.find((p) => p.ProcessId === -1);
    const existingTagRemove = list.find((p) => p.ProcessId === -2);
    const commonProcess = list.find((p) => p.ProcessId === -3);
    const dimensionProcess = list.find((p) => p.ProcessId === -4);

    // only real user processes
    const normalProcesses = list.filter((p) => p.ProcessId >= 0);
    const hasRealProcesses = normalProcesses.length > 0;
    const firstRealDueDate = Number(normalProcesses?.[0]?.DueDates || 0);
    const tagAllocation =
      existingTagAllocation || buildTagAllocation(firstRealDueDate);
    const tagRemove = existingTagRemove || buildTagRemove(0);

    // final correct order
    const finalList = [
      ...(hasRealProcesses
        ? [tagAllocation, ...normalProcesses, tagRemove]
        : []),
      ...(commonProcess ? [commonProcess] : []),
      ...(dimensionProcess ? [dimensionProcess] : []),
    ];

    // ✅ fix ProcessOrder sequentially
    return finalList.map((p, index) => ({
      ...p,
      ProcessOrder: index,
    }));
  };

  const mergeTemplateWithExistingProcessData = (
    templateProcesses: any[] = [],
    existingProcesses: any[] = []
  ) => {
    const existingByProcessId = new Map<number, any>();
    existingProcesses.forEach((proc: any) => {
      existingByProcessId.set(Number(proc?.ProcessId), proc);
    });

    return templateProcesses.map((templateProc: any) => {
      const existing = existingByProcessId.get(Number(templateProc?.ProcessId));
      return {
        ...templateProc,
        IsFromTemplate: Number(templateProc?.ProcessId) >= 0,
        ViewType: templateProc?.ViewType ?? 1,
        fileIndices: Array.isArray(existing?.fileIndices)
          ? [...existing.fileIndices]
          : Array.isArray(templateProc?.fileIndices)
            ? [...templateProc.fileIndices]
            : [],
        DimensionDetails:
          existing?.DimensionDetails ?? templateProc?.DimensionDetails,
      };
    });
  };

  // Track files for deletion from the removed process(es)
  const collectDeletedFiles = (processes: any[]) => {
    const oldFiles: any[] = [];
    const newFiles: any[] = [];
    processes.forEach((proc) => {
      (proc.fileIndices || []).forEach((fi: any) => {
        // Skip common images when deleting/re-arranging processes.
        // Their lifecycle is managed via the common pool in Step 3.
        const isTrue = (val: any) =>
          val === true || String(val).toLowerCase() === 'true';
        if (isTrue(fi.IsDefault) || isTrue(fi.isDefault)) return;

        (fi.FileDetail || []).forEach((f: any) => {
          if (f.FileId && Number(f.FileId) > 0) {
            oldFiles.push(f);
          } else {
            newFiles.push(f);
          }
        });
      });
    });

    if (oldFiles.length > 0) {
      setOldDeletedFiles([...productStore.oldDeletedFiles, ...oldFiles]);
    }
    if (newFiles.length > 0) {
      setNewDeletedFiles([...productStore.newDeletedFiles, ...newFiles]);
    }
  };

  useEffect(() => {
    GetAllProcessService();
    GetAllTemplatesService(Number(getTenantIdFromLocalStorage()));
  }, []);

  useEffect(() => {
    if (initalValues?.Process?.length > 0) {
      setSelectedProcesses(normalizeAndReorder([...initalValues.Process]));
    }
  }, [initalValues.Process]);

  useEffect(() => {
    if (processTemplateDetails?.length > 0) {
      setSelectedTemplateProcessIds(
        processTemplateDetails
          .map((process) => Number(process?.ProcessId))
          .filter((processId) => processId >= 0)
      );
      setDuplicateTemplateProcessId(null);
      const existingFromFormik = formikRef.current?.values?.Process || [];
      const merged = mergeTemplateWithExistingProcessData(
        processTemplateDetails,
        existingFromFormik.length > 0 ? existingFromFormik : selectedProcesses
      );
      setSelectedProcesses(normalizeAndReorder(merged));
    }
  }, [processTemplateDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // if (!isScroll) return;
    if (!selectedProcessDetails || selectedProcessDetails.Id === -1) return;
    if (
      selectedProcesses.length === 0 &&
      selectedProcessDetails &&
      selectedProcessDetails.Id !== -1
    ) {
      setSelectedProcesses([
        {
          Id: -1,
          ProcessId: -1,
          DueDates: 0,
          ProcessOrder: 0,
          ProcessName: 'Tag Allocation',
          Type: -1,
          ShortName: 'ALLOCATE',
          StandardTime: '',
          ViewType: 1,
        },
        {
          ...selectedProcessDetails,
          DueDates: 0,
          ViewType: selectedProcessDetails.ViewType ?? 1,
        },
        {
          Id: -1,
          ProcessId: -2,
          DueDates: 0,
          ProcessOrder: 0,
          ProcessName: 'Tag Remove',
          Type: -2,
          ShortName: 'REMOVE',
          StandardTime: '',
          ViewType: 1,
        },
      ]);
    }

    if (selectedProcesses.length > 0 && selectedProcessDetails) {
      setSelectedProcesses((prev) => {
        const isProcessExist = prev.find(
          (p) => p.ProcessId === selectedProcessDetails.ProcessId
        );

        if (!isProcessExist) {
          setDuplicateTemplateProcessId(null);
          // Remove ALL existing Tag Remove entries (ProcessId === -2) first
          const base = prev.filter((p) => p.ProcessId !== -2);
          return normalizeAndReorder([
            ...base,
            {
              ...selectedProcessDetails,
              ProcessOrder: base.length,
            },
            {
              Id: -1,
              ProcessId: -2,
              DueDates: 0,
              ProcessOrder: 0,
              ProcessName: 'Tag Remove',
              Type: -2,
              ShortName: 'REMOVE',
              StandardTime: '',
              ViewType: 0,
            },
          ]);
        }

        if (
          selectedTemplateProcessIds.includes(
            Number(selectedProcessDetails.ProcessId)
          )
        ) {
          setDuplicateTemplateProcessId(
            Number(selectedProcessDetails.ProcessId)
          );
        }

        return prev;
      });
    }
  }, [selectedProcessDetails, selectedTemplateProcessIds]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleSelectChange = async (e: any) => {
    setIsScroll(false);
    const selectedProcessId = Number(e.target.value);
    const isExistingProcessDuplicate = selectedProcesses.some(
      (process) =>
        Number(process?.ProcessId) === selectedProcessId &&
        Number(process?.ProcessId) >= 0
    );

    if (isExistingProcessDuplicate) {
      setDuplicateTemplateProcessId(selectedProcessId);
      return;
    }

    setDuplicateTemplateProcessId(null);
    GetProcessByIdService(selectedProcessId);
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        'SelectedProcesses',
        selectedProcesses.length + 1
      );
    }
  };

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        'SelectedProcesses',
        processTemplateDetails.length
      );
    }
  }, [processTemplateDetails]);

  const handleSubmit = async (values: any) => {
    // UI-level safety: keep IsPrint aligned with selected print process (Type=2)
    // before handing payload to next step/store.
    const hasPrintProcess = selectedProcesses.some(
      (process: any) => Number(process?.Type) === 2
    );
    let tempValues = {
      ...values,
      IsPrint: hasPrintProcess || loadStepThree,
    };
    const finalPayload = {
      ...tempValues,
      Process: [
        ...selectedProcesses
          .filter((p: any) => p.ProcessId !== -3 && p.ProcessId !== -4) // Prevent duplication
          .map((rest) => {
            return {
              ...rest,
              ViewType:
                rest?.ViewType === -1 || rest?.ViewType === undefined
                  ? 1
                  : rest?.ViewType,
              fileIndices: (Number(rest?.ProcessId) === -1 || Number(rest?.ProcessId) === -2)
                ? []
                : values?.Process?.find(
                  (p: any) => Number(p?.ProcessId) === Number(rest?.ProcessId)
                )?.fileIndices || rest?.fileIndices || [],
            };
          }),
        {
          ProcessId: -3,
          DueDates: 0,
          fileIndices: (values?.Process?.find((p: any) => Number(p?.ProcessId) === -3)?.fileIndices || values?.CommonProcess || [])
            ?.filter(
              (obj: any, index: number, self: any[]) =>
                index === self.findIndex((el) => (el.FileDetail?.[0]?.Heading === obj.FileDetail?.[0]?.Heading) || (el.TabIndex === obj.TabIndex))
            )
            ?.slice(0, 5),
        },
        {
          ProcessId: -4,
          DueDates: 0,
          fileIndices: (values?.Process?.find((p: any) => Number(p?.ProcessId) === -4)?.fileIndices || []),
        },
      ],
    };
    // console.log('Step 2 Payload:', finalPayload);
    if (props.id < 0 && hasPrintProcess) {
      await GetTeplarByProductIdService(
        Number(getTenantIdFromLocalStorage()),
        values.ProductName
      );
    }
    props.next(finalPayload);
  };

  const handleSubmit1 = (values: any, isNotCreateNew: boolean = true) => {
    // Same guard for "save and next" path so both submit entry points behave identically.
    const hasPrintProcess = selectedProcesses.some(
      (process: any) => Number(process?.Type) === 2
    );
    let tempValues = {
      ...values,
    };
    props.handleSaveAndNext(
      {
        ...tempValues,
        Process: selectedProcesses.map((rest) => ({
          ...rest,
          ViewType:
            rest?.ViewType === -1 || rest?.ViewType === undefined
              ? 1
              : rest?.ViewType,
          fileIndices: (Number(rest?.ProcessId) === -1 || Number(rest?.ProcessId) === -2)
            ? []
            : rest.fileIndices || [],
        })),
        IsPrint: hasPrintProcess || loadStepThree,
      },
      true
    );
    props.setCloseModal(false);
  };

  const getDueDateViolationIndexes = (processes: any[]): number[] => {
    const invalidIndexes: number[] = [];
    for (let index = 1; index < processes.length; index++) {
      const currentDueDate = Number(processes[index]?.DueDates || 0);
      const previousDueDate = Number(processes[index - 1]?.DueDates || 0);
      if (currentDueDate > previousDueDate) {
        invalidIndexes.push(index);
      }
    }
    return invalidIndexes;
  };

  const validateDueDates = (processes: any[]): boolean => {
    return getDueDateViolationIndexes(processes).length === 0;
  };

  const validateStandardTime = (processes: any[]): boolean => {
    const timeRegex = /^\d{1,4}:(?:[0-5][0-9])$/;

    return processes.every((process) => {
      if (process.Type >= 0) {
        const time = (process.StandardTime || '').trim();
        if (!time) return true;
        return timeRegex.test(time);
      }
      return true;
    });
  };

  const handleInputChange = (rowIndex: number, value: number) => {
    setSelectedProcesses((prevProcesses) => {
      const lastIndex = prevProcesses.length - 1;

      return prevProcesses.map((process, index) => {
        // keep first two in sync if editing 0 or 1
        if (rowIndex === 0 || rowIndex === 1) {
          if (index === 0 || index === 1) {
            return { ...process, DueDates: value };
          }
        }

        // keep second-last in sync when editing it
        if (rowIndex === lastIndex - 1 && index === lastIndex - 1) {
          return { ...process, DueDates: value };
        }

        return index === rowIndex ? { ...process, DueDates: value } : process;
      });
    });
  };

  const handleTimeInputChange = (rowIndex: number, value: string) => {
    // Remove invalid characters
    let cleaned = value.replace(/[^0-9:]/g, '');

    // Allow only one colon
    const colonIndex = cleaned.indexOf(':');
    if (colonIndex !== -1) {
      cleaned =
        cleaned.slice(0, colonIndex + 1) +
        cleaned.slice(colonIndex + 1).replace(/:/g, '');
    }

    let hours = '';
    let seconds = '';

    if (cleaned.includes(':')) {
      [hours, seconds] = cleaned.split(':');
    } else {
      hours = cleaned;
    }

    // Hours: max 4 digits
    hours = hours.slice(0, 4);

    // Seconds: max 2 digits
    seconds = seconds.slice(0, 2);

    // 🚫 Cap seconds to 59
    if (seconds.length === 2) {
      const secNum = Number(seconds);
      if (secNum > 59) {
        seconds = '59';
      }
    }

    const finalValue = cleaned.includes(':') ? `${hours}:${seconds}` : hours;

    setSelectedProcesses((prev) =>
      prev.map((process, index) =>
        index === rowIndex ? { ...process, StandardTime: finalValue } : process
      )
    );
  };

  const handleViewTypeChange = (rowIndex: number, value: number) => {
    setSelectedProcesses((prev) =>
      prev.map((p, i) => (i === rowIndex ? { ...p, ViewType: value } : p))
    );
  };

  const moveProcessUpHandler = (index: any) => {
    if (index === 0) return;
    const newList = [...selectedProcesses];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    if (index === newList.length - 2) {
      newList[index + 1].DueDates = newList[index].DueDates;
    }
    if (index === 2) {
      newList[0].DueDates = newList[1].DueDates;
    }
    newList[newList.length - 1].DueDates = 0;
    const updatedList = setProcessOrder(newList);
    setSelectedProcesses(normalizeAndReorder([...updatedList]));
  };

  const moveProcessDownHandler = (index: number) => {
    if (index === selectedProcesses.length - 1) return;
    const newList = [...selectedProcesses];

    [newList[index + 1], newList[index]] = [newList[index], newList[index + 1]];
    if (index === 1) {
      newList[0].DueDates = newList[1].DueDates;
    }
    if (index === newList.length - 3) {
      newList[newList.length - 1].DueDates =
        newList[newList.length - 2].DueDates;
    }
    newList[newList.length - 1].DueDates = 0;
    const updatedList = setProcessOrder(newList);
    setSelectedProcesses(normalizeAndReorder([...updatedList]));
  };

  const removeProcessHandler = (index: any) => {
    setIsScroll(false);

    if (selectedProcesses.length <= 3 && index === undefined) {
      // Case where we might be clearing everything
      collectDeletedFiles(selectedProcesses);
      setSelectedProcesses([]);

      // Clear Formik values as well
      if (formikRef.current) {
        formikRef.current.setFieldValue('Process', []);
      }
    } else {
      const removedProcess = selectedProcesses[index];
      if (removedProcess) {
        collectDeletedFiles([removedProcess]);
        if (
          Number(removedProcess.ProcessId) === Number(duplicateTemplateProcessId)
        ) {
          setDuplicateTemplateProcessId(null);
        }

        // Remove from Formik values to prevent data from being restored if re-added
        if (formikRef.current) {
          const currentFormProcess = formikRef.current.values.Process || [];
          formikRef.current.setFieldValue(
            'Process',
            currentFormProcess.filter(
              (p: any) =>
                Number(p.ProcessId) !== Number(removedProcess.ProcessId)
            )
          );
        }
      }

      const newList = selectedProcesses.filter((_, i) => i !== index);
      if (formikRef.current) {
        formikRef.current.setFieldValue(
          'SelectedProcesses',
          selectedProcesses.length - 1
        );
      }
      if (newList.length > 0) {
        if (newList[0]) newList[0].DueDates = newList[1]?.DueDates || 0;
        if (newList[newList.length - 1])
          newList[newList.length - 1].DueDates = 0;
      }
      const updatedList = setProcessOrder(newList);
      setSelectedProcesses(normalizeAndReorder([...updatedList]));
    }
  };

  const setProcessOrder = (list: any) => {
    const newList = [...list];
    let positiveOrder = 1;
    let negativeOrder = -1;

    newList.forEach((process) => {
      if (process.ProcessId >= 0) {
        process.ProcessOrder = positiveOrder;
        positiveOrder++;
      } else {
        process.ProcessOrder = negativeOrder;
        negativeOrder--;
      }
    });
    return newList;
  };

  const actionIcons: IActionFormatter[] = [
    {
      dataField: 'up',
      handler: moveProcessUpHandler,
      icon: <UpIcon />,
      isDisable: false,
      title: 'up',
      action: 'up',
      varient: 'primary',
    },
    {
      dataField: 'down',
      handler: moveProcessDownHandler,
      icon: <DownIcon />,
      isDisable: false,
      title: 'down',
      action: 'down',
      varient: 'primary',
    },
    {
      dataField: 'delete',
      handler: removeProcessHandler,
      icon: <RemoveIcon />,
      isDisable: false,
      title: 'delete',
      action: 'delete',
      varient: 'danger-new',
    },
  ];

  useEffect(() => {
    GetDimensionColumnService(getTenantAccessFromLocalStorage());
  }, []);

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={{
          ...props.initalValues,
          ProcessTemplate: '-1',
          selectedProcesses:
            props.initalValues.selectedProcesses?.map((p: any) => ({
              ...p,
              StandardTime: p.StandardTime || '00:00',
            })) || [],
        }}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        enableReinitialize
        validate={(values) => {
          const errors: any = {};
          const realProcesses = selectedProcesses.filter(p => p.ProcessId >= 0);
          if (realProcesses.length === 0) {
            errors.selectedProcesses = formatMessage(
              'select_a_template_or_process'
            );
          } else if (!validateDueDates(selectedProcesses)) {
            errors.selectedProcesses = formatMessage(
              'due_dates_should_be_in_decreasing_order'
            );
          } else if (!validateStandardTime(selectedProcesses)) {
            errors.selectedProcesses = formatMessage(
              'invalid_standard_time_format'
            );
          }
          return errors;
        }}
      >
        {({
          values,
          isValid,
          handleSubmit,
          errors,
          setFieldValue,
          setErrors,
          resetForm,
          submitCount,
        }) => {
          const tableProcesses = selectedProcesses.filter(
            (p) => p.ProcessId != -3 && p.ProcessId != -4
          );
          const dueDateViolationIndexes =
            getDueDateViolationIndexes(tableProcesses);
          const hasDueDateOrderError = dueDateViolationIndexes.length > 0;
          const showProcessErrorMessage =
            submitCount > 0 &&
            !!errors.selectedProcesses &&
            !processState.inProgress &&
            (!validateDueDates(selectedProcesses) ||
              selectedProcesses.filter((p: any) => p.ProcessId >= 0).length ===
                0 ||
              !validateStandardTime(selectedProcesses));

          return (
            <Form
              id="formBody"
              onSubmit={handleSubmit}
            >
            {props.isLoading && <FormLoader loading={props.isLoading} />}

            <Modal.Body>
              <div className="wizard overflow-hidden">
                <div className="wizard-inner">
                  <div className="connecting-line"></div>
                  <ul
                    className="nav nav-tabs"
                    role="tablist"
                  >
                    <li
                      role="presentation"
                      className="active"
                    >
                      <a
                        data-toggle="tab"
                        aria-controls="step1"
                        role="tab"
                        aria-expanded="true"
                      >
                        <span className="round-tab">
                          <RoundTabIcon />
                        </span>
                        <i>{formatMessage('step1')}</i>
                      </a>
                    </li>
                    <li
                      role="presentation"
                      className="active"
                    >
                      <a
                        data-toggle="tab"
                        aria-controls="step2"
                        role="tab"
                        aria-expanded="false"
                      >
                        <span className="round-tab">2</span>{' '}
                        <i>{formatMessage('step2')}</i>
                      </a>
                    </li>
                    <li
                      role="presentation"
                      className="active"
                    >
                      <a
                        data-toggle="tab"
                        aria-controls="step3"
                        role="tab"
                        aria-expanded="false"
                      >
                        <span className="round-tab">3</span>{' '}
                        <i>{formatMessage('step3')}</i>
                      </a>
                    </li>
                    <li
                      role="presentation"
                      className="disabled"
                    >
                      <a
                        data-toggle="tab"
                        aria-controls="step4"
                        role="tab"
                        aria-expanded="false"
                      >
                        <span className="round-tab">4</span>{' '}
                        <i>{formatMessage('step4')}</i>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="row px-3">
                <div className="col-4">
                  <div className="form-group formSet">
                    <label>
                      <FormattedMessage id="product_id" />
                    </label>
                    <input
                      type="text"
                      value={props.initalValues.ProductName}
                      placeholder="-"
                      className="form-control"
                      disabled
                    />
                  </div>
                </div>
                <div className="col-4">
                  <div className="form-group formSet">
                    <Label
                      required={false}
                      label="select_template"
                    />
                    <Field
                      name="ProcessTemplate"
                      className="form-control"
                      as={FormikFormSelect}
                      options={allAvailableProcessTemplates}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        setIsScroll(false);
                        GetProcessTemplateByIdService(
                          Number(getTenantAccessFromLocalStorage()),
                          parseInt(e.target.value)
                        );
                        setDuplicateTemplateProcessId(null);
                        // Keep dropdown on default option after applying template.
                        setFieldValue('ProcessTemplate', '-1');
                        setErrors({});
                      }}
                    />
                  </div>

                  <div>
                    {showProcessErrorMessage ? (
                      <div className="text-danger errorMessage">
                        {typeof errors.selectedProcesses === 'string'
                          ? errors.selectedProcesses
                          : String(errors.selectedProcesses)}
                      </div>
                    ) : (
                      <div>{''}</div>
                    )}
                  </div>
                </div>
                <div className="col-4">
                  <Label
                    required={false}
                    label="add_process"
                  />
                  <div className="form-group formSet">
                    <Field
                      name="ProcessOption"
                      className="form-control"
                      as={FormikFormSelect}
                      options={allProcessOptions}
                      onChange={handleSelectChange}
                    />
                  </div>
                </div>
              </div>
              <div className="px-3  step-two-tab-content">
                <div className="tableDevices process-product tableDesign">
                  <div className="table-responsive">
                    <div className="react-bootstrap-table">
                      <table className="table table-bordered table-striped border">
                        <thead>
                          <tr>
                            {processHeaders.map((item, index) => {
                              return <th key={index}>{formatMessage(item)}</th>;
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {tableProcesses?.length > 0 ?
                            tableProcesses.map((process, rowIndex) => {
                              const isDueDateViolationRow =
                                showProcessErrorMessage &&
                                hasDueDateOrderError &&
                                dueDateViolationIndexes.includes(rowIndex);
                              const isDuplicateTemplateProcessRow =
                                process.ProcessId >= 0 &&
                                Number(process.ProcessId) ===
                                  Number(duplicateTemplateProcessId);
                              return (
                                <tr
                                  key={rowIndex}
                                  className={[
                                    isDueDateViolationRow
                                      ? 'due-date-order-error-row'
                                      : '',
                                    isDuplicateTemplateProcessRow
                                      ? 'duplicate-template-process-row'
                                      : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                >
                                  <td className="show-Elipses">
                                    {process.ProcessName}
                                  </td>
                                  <td>{process.ShortName}</td>
                                  <td>
                                    {process.Type >= 0
                                      ? formatMessage(
                                        ProcessTypeEnum[process.Type]
                                      )
                                      : process.Type === -1
                                        ? formatMessage('Tag Allocation')
                                        : formatMessage('Tag Remove')}
                                  </td>
                                  <td>
                                    <input
                                      className={`form-control ${
                                        isDueDateViolationRow
                                          ? 'is-invalid due-date-order-error-input'
                                          : ''
                                      }`}
                                      type="number"
                                      value={process?.DueDates}
                                      min={0}
                                      max={99}
                                      onChange={(e) => {
                                        handleInputChange(
                                          rowIndex,
                                          parseFloat(e.target.value) || 0
                                        );
                                      }}
                                      onInvalid={(e) =>
                                        (
                                          e.target as HTMLInputElement
                                        ).setCustomValidity(
                                          formatMessage('max_allowed_value_99')
                                        )
                                      }
                                      onInput={(e) =>
                                        (
                                          e.target as HTMLInputElement
                                        ).setCustomValidity('')
                                      }
                                      disabled={
                                        process.ProcessId === -1 ||
                                        process.ProcessId === -2
                                      }
                                    />
                                  </td>
                                  <td>
                                    {process.Type >= 0 ? (
                                      <input
                                        data-testid="testStandardTime"
                                        className="form-control"
                                        type="text"
                                        placeholder={
                                          props.id < 0 ? 'HHHH:MM' : '0000:00'
                                        }
                                        value={
                                          process?.StandardTime === '00:00'
                                            ? ''
                                            : process?.StandardTime || ''
                                        }
                                        maxLength={7} // 4 digits + ':' + 2 digits
                                        inputMode="numeric"
                                        onChange={(e) => {
                                          handleTimeInputChange(
                                            rowIndex,
                                            e.target.value
                                          );
                                        }}
                                        onKeyDown={(e) => {
                                          const navigationKeys = [
                                            'Backspace',
                                            'Delete',
                                            'ArrowLeft',
                                            'ArrowRight',
                                            'Tab',
                                            'Home',
                                            'End',
                                          ];

                                          if (e.ctrlKey || e.metaKey) return;

                                          if (navigationKeys.includes(e.key))
                                            return;

                                          if (!/^[0-9:]$/.test(e.key)) {
                                            e.preventDefault();
                                          }
                                        }}
                                      />
                                    ) : (
                                      '--'
                                    )}
                                  </td>

                                  <td>
                                    {process.Type >= 0 ? (
                                      <Field name={`ViewType-${rowIndex}`}>
                                        {({ field, form }) => (
                                          <select
                                            className="form-control"
                                            {...field}
                                            value={process.ViewType ?? 1}
                                            onChange={(e) => {
                                              const selectedId = parseInt(
                                                e.target.value,
                                                10
                                              );

                                              form.setFieldValue(
                                                field.name,
                                                selectedId
                                              );

                                              handleViewTypeChange(
                                                rowIndex,
                                                selectedId
                                              );
                                            }}
                                          >
                                            {ViewTypeOptions.map((opt) => (
                                              <option
                                                key={opt.id}
                                                value={opt.id}
                                                disabled={opt.disabled}
                                              >
                                                {formatMessage(
                                                  opt.value.toLowerCase()
                                                )}
                                              </option>
                                            ))}
                                          </select>
                                        )}
                                      </Field>
                                    ) : (
                                      '--'
                                    )}
                                  </td>
                                  <td className="arrow-up-down">
                                    <div className="d-flex">
                                      {actionIcons.map(
                                        (
                                          action: IActionFormatter,
                                          index: number
                                        ) => {
                                          return (
                                            <button
                                              className={`btn btn-light-${action.varient} btn-sm `}
                                              key={index}
                                              title={formatMessage(
                                                action.title
                                              )}
                                              type="button"
                                              disabled={
                                                process.ProcessId === -1 ||
                                                process.ProcessId === -2 ||
                                                (action.dataField === 'up' &&
                                                  rowIndex === 1) ||
                                                (action.dataField === 'down' &&
                                                  rowIndex ===
                                                  selectedProcesses.filter(
                                                    (p) =>
                                                      p.ProcessId !== -3 &&
                                                      p.ProcessId !== -4
                                                  ).length -
                                                  2)
                                              }
                                              onClick={() => {
                                                action.handler(rowIndex);
                                              }}
                                            >
                                              {action.icon}
                                            </button>
                                          );
                                        }
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            }) :
                            (
                              <tr className="empty-row">
                                <td
                                  colSpan={(processHeaders?.length || 0) + 1}
                                  className="empty-message text-center w-100"
                                >
                                  {formatMessage('no_process_template_found')},<br/> {formatMessage('no_process_template_found_message')}
                                </td>
                              </tr>
                            )
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="d-flex justify-content-center">
                <div className="row tab-action g-0">
                  <div className="col-12">
                    <Button
                      data-testid="back"
                      variant="light-danger"
                      onClick={() => {
                        resetProcessTemplateDetail();
                        resetProcessDetail();
                        props.prev({
                          ...values,
                          Process: selectedProcesses,
                        });
                      }}
                      disabled={props.isLoading}
                    >
                      <FormattedMessage id="back" />
                    </Button>
                    <>
                      <LoaderButton
                        type="submit"
                        id="SaveTenant"
                        // isLoading={props.isLoading}
                        text="next"
                        onClick={() => {
                          // localStorage.setItem(
                          //   'selectedProcesses',
                          //   JSON.stringify(selectedProcesses)
                          // );
                          resetProcessTemplateDetail();
                          resetProcessDetail();
                        }}
                      />
                    </>
                  </div>
                </div>
              </div>
            </Modal.Footer>
          </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default observer(StepTwo);
